import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetTaxConfigurationsQuery,
  useCreateTaxConfigurationMutation,
  useUpdateTaxConfigurationMutation,
  useDeleteTaxConfigurationMutation,
  useCalculateTaxMutation,
  useGetStatesByCountryQuery,
} from '../../services/taxApi';
import { useGetMyCompanyQuery } from '../../services/companyApi';
import {
  setCreateModalOpen,
  setEditModalOpen,
  setDeleteModalOpen,
  setCalculationModalOpen,
  setSelectedConfigId,
  updateTaxFormData,
  resetTaxFormData,
  updateTaxFilters,
  resetTaxFilters,
  addTaxSlab,
  updateTaxSlab,
  removeTaxSlab,
} from '../../store/slices/taxSlice';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalculatorIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

const TaxMaster = () => {
  const dispatch = useDispatch();
  const {
    taxFormData,
    taxFilters,
    ui: {
      isCreateModalOpen,
      isEditModalOpen,
      isDeleteModalOpen,
      isCalculationModalOpen,
      selectedConfigId,
    },
  } = useSelector((state) => state.tax);

  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [calculationData, setCalculationData] = useState({
    country: '',
    state: '',
    taxType: '',
    salary: '',
    date: '',
  });

  // New state for modern UI
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [itemsPerPage] = useState(10);

  // API hooks
  const { data: taxConfigs, isLoading, refetch } = useGetTaxConfigurationsQuery({
    page: currentPage,
    limit: 10,
    ...taxFilters,
  });

  const { data: states } = useGetStatesByCountryQuery(taxFilters.country, {
    skip: !taxFilters.country,
  });

  const [createTaxConfig, { isLoading: isCreating }] = useCreateTaxConfigurationMutation();
  const [updateTaxConfig, { isLoading: isUpdating }] = useUpdateTaxConfigurationMutation();
  const [deleteTaxConfig, { isLoading: isDeleting }] = useDeleteTaxConfigurationMutation();
  const [calculateTax, { isLoading: isCalculating }] = useCalculateTaxMutation();

  // Get company data for currency
  const { data: companyData } = useGetMyCompanyQuery();
  const company = companyData?.data;
  const companyCurrency = company?.currency || 'USD';

  // Countries list
  const countries = useMemo(() => [
    { code: 'US', name: 'United States' },
    { code: 'IN', name: 'India' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'JP', name: 'Japan' },
  ], []);

  // Filtered data logic
  const filteredData = useMemo(() => {
    if (!taxConfigs?.data) return [];

    let filtered = [...taxConfigs.data];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(config =>
        config.taxName?.toLowerCase().includes(searchLower) ||
        getTaxTypeDisplayName(config.taxType)?.toLowerCase().includes(searchLower) ||
        config.country?.toLowerCase().includes(searchLower) ||
        config.state?.toLowerCase().includes(searchLower) ||
        countries.find(c => c.code === config.country)?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Country filter
    if (taxFilters.country) {
      filtered = filtered.filter(config => config.country === taxFilters.country);
    }

    // State filter
    if (taxFilters.state) {
      filtered = filtered.filter(config => config.state === taxFilters.state);
    }

    // Tax Type filter
    if (taxFilters.taxType) {
      filtered = filtered.filter(config => config.taxType === taxFilters.taxType);
    }

    return filtered;
  }, [taxConfigs?.data, searchTerm, taxFilters, countries]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!taxConfigs?.data) return { total: 0, active: 0, inactive: 0 };

    const total = taxConfigs.data.length;
    const active = taxConfigs.data.filter(config => config.isActive).length;
    const inactive = total - active;

    return { total, active, inactive };
  }, [taxConfigs?.data]);

  // Export functions
  const exportToCSV = () => {
    const headers = ['Tax Name', 'Tax Type', 'Country', 'State', 'Effective From', 'Effective To', 'Status', 'Slabs Count'];
    const csvData = filteredData.map(config => [
      config.taxName,
      getTaxTypeDisplayName(config.taxType),
      countries.find(c => c.code === config.country)?.name || config.country,
      config.state,
      formatDate(config.effectiveFrom),
      config.effectiveTo ? formatDate(config.effectiveTo) : 'No End Date',
      config.isActive ? 'Active' : 'Inactive',
      config.slabs?.length || 0
    ]);

    const csvContent = [headers, ...csvData].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tax-configurations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

const taxTypeOptions = [
  // US
  { code: 'US_INCOME_TAX', country: 'US', type: 'tax' },
  { code: 'US_FEDERAL_WITHHOLDING', country: 'US', type: 'withholding' },
  { code: 'US_STATE_INCOME_TAX', country: 'US', type: 'tax' },
  { code: 'US_SOCIAL_SECURITY', country: 'US', type: 'social' },
  { code: 'US_MEDICARE', country: 'US', type: 'social' },

  // India
  { code: 'IN_INCOME_TAX', country: 'IN', type: 'tax' },
  { code: 'IN_PROFESSIONAL_TAX', country: 'IN', type: 'tax' },
  { code: 'IN_TDS', country: 'IN', type: 'withholding' },
  { code: 'IN_PF', country: 'IN', type: 'social' },
  { code: 'IN_ESIC', country: 'IN', type: 'social' },
  { code: 'IN_LWF', country: 'IN', type: 'other' },

  // UK
  { code: 'UK_INCOME_TAX', country: 'UK', type: 'tax' },
  { code: 'UK_NATIONAL_INSURANCE', country: 'UK', type: 'social' },
  { code: 'UK_PENSION_CONTRIBUTIONS', country: 'UK', type: 'social' },

  // Canada
  { code: 'CA_INCOME_TAX', country: 'CA', type: 'tax' },
  { code: 'CA_PROVINCIAL_TAX', country: 'CA', type: 'tax' },
  { code: 'CA_CPP', country: 'CA', type: 'social' },
  { code: 'CA_EI', country: 'CA', type: 'social' },

  // Australia
  { code: 'AU_INCOME_TAX', country: 'AU', type: 'tax' },
  { code: 'AU_MEDICARE_LEVY', country: 'AU', type: 'tax' },
  { code: 'AU_SUPERANNUATION', country: 'AU', type: 'social' },

  // Germany
  { code: 'DE_INCOME_TAX', country: 'DE', type: 'tax' },
  { code: 'DE_SOCIAL_SECURITY', country: 'DE', type: 'social' },
  { code: 'DE_HEALTH_INSURANCE', country: 'DE', type: 'social' },
  { code: 'DE_UNEMPLOYMENT_INSURANCE', country: 'DE', type: 'social' },
  { code: 'DE_PENSION_INSURANCE', country: 'DE', type: 'social' },
  { code: 'DE_LONG_TERM_CARE_INSURANCE', country: 'DE', type: 'social' },
  { code: 'DE_SOLIDARITY_SURCHARGE', country: 'DE', type: 'other' },

  // France
  { code: 'FR_INCOME_TAX', country: 'FR', type: 'tax' },
  { code: 'FR_SOCIAL_SECURITY', country: 'FR', type: 'social' },
  { code: 'FR_HEALTH_INSURANCE', country: 'FR', type: 'social' },
  { code: 'FR_UNEMPLOYMENT_INSURANCE', country: 'FR', type: 'social' },
  { code: 'FR_PENSION_CONTRIBUTIONS', country: 'FR', type: 'social' },

  // Japan
  { code: 'JP_INCOME_TAX', country: 'JP', type: 'tax' },
  { code: 'JP_RESIDENT_TAX', country: 'JP', type: 'tax' },
  { code: 'JP_HEALTH_INSURANCE', country: 'JP', type: 'social' },
  { code: 'JP_PENSION_INSURANCE', country: 'JP', type: 'social' },
  { code: 'JP_UNEMPLOYMENT_INSURANCE', country: 'JP', type: 'social' },
];


  const getTaxTypeDisplayName = (taxType) => {
    // Handle both string and object formats for backward compatibility
    const code = typeof taxType === 'object' ? taxType.code : taxType;

    const displayNames = {
      // US Tax Types
      'US_INCOME_TAX': 'US Income Tax',
      'US_FEDERAL_WITHHOLDING': 'US Federal Withholding',
      'US_STATE_INCOME_TAX': 'US State Income Tax',
      'US_SOCIAL_SECURITY': 'US Social Security',
      'US_MEDICARE': 'US Medicare',

      // Indian Tax Types
      'IN_INCOME_TAX': 'India Income Tax',
      'IN_PROFESSIONAL_TAX': 'India Professional Tax',
      'IN_TDS': 'India TDS',
      'IN_PF': 'India PF',
      'IN_ESIC': 'India ESIC',
      'IN_LWF': 'India LWF',

      // UK Tax Types
      'UK_INCOME_TAX': 'UK Income Tax',
      'UK_NATIONAL_INSURANCE': 'UK National Insurance',
      'UK_PENSION_CONTRIBUTIONS': 'UK Pension Contributions',

      // Canadian Tax Types
      'CA_INCOME_TAX': 'Canada Income Tax',
      'CA_PROVINCIAL_TAX': 'Canada Provincial Tax',
      'CA_CPP': 'Canada CPP',
      'CA_EI': 'Canada EI',

      // Australian Tax Types
      'AU_INCOME_TAX': 'Australia Income Tax',
      'AU_MEDICARE_LEVY': 'Australia Medicare Levy',
      'AU_SUPERANNUATION': 'Australia Superannuation',

      // German Tax Types
      'DE_INCOME_TAX': 'Germany Income Tax',
      'DE_SOCIAL_SECURITY': 'Germany Social Security',
      'DE_HEALTH_INSURANCE': 'Germany Health Insurance',
      'DE_UNEMPLOYMENT_INSURANCE': 'Germany Unemployment Insurance',
      'DE_PENSION_INSURANCE': 'Germany Pension Insurance',
      'DE_LONG_TERM_CARE_INSURANCE': 'Germany Long-term Care Insurance',
      'DE_SOLIDARITY_SURCHARGE': 'Germany Solidarity Surcharge',

      // French Tax Types
      'FR_INCOME_TAX': 'France Income Tax',
      'FR_SOCIAL_SECURITY': 'France Social Security',
      'FR_HEALTH_INSURANCE': 'France Health Insurance',
      'FR_UNEMPLOYMENT_INSURANCE': 'France Unemployment Insurance',
      'FR_PENSION_CONTRIBUTIONS': 'France Pension Contributions',

      // Japanese Tax Types
      'JP_INCOME_TAX': 'Japan Income Tax',
      'JP_RESIDENT_TAX': 'Japan Resident Tax',
      'JP_HEALTH_INSURANCE': 'Japan Health Insurance',
      'JP_PENSION_INSURANCE': 'Japan Pension Insurance',
      'JP_UNEMPLOYMENT_INSURANCE': 'Japan Unemployment Insurance',
    };

    return displayNames[code] || code.replace(/_/g, ' ');
  };

  // Map client-side tax type codes to server-side enum values
  const mapTaxTypeToServer = (clientTaxType) => {
    const mapping = {
      // US
      'US_INCOME_TAX': 'INCOME_TAX',
      'US_FEDERAL_WITHHOLDING': 'FEDERAL_WITHHOLDING',
      'US_STATE_INCOME_TAX': 'STATE_INCOME_TAX',
      'US_SOCIAL_SECURITY': 'SOCIAL_SECURITY',
      'US_MEDICARE': 'MEDICARE',

      // India
      'IN_INCOME_TAX': 'INCOME_TAX',
      'IN_PROFESSIONAL_TAX': 'PROFESSIONAL_TAX',
      'IN_TDS': 'TDS',
      'IN_PF': 'PF',
      'IN_ESIC': 'ESIC',
      'IN_LWF': 'LWF',

      // UK
      'UK_INCOME_TAX': 'INCOME_TAX',
      'UK_NATIONAL_INSURANCE': 'NATIONAL_INSURANCE',
      'UK_PENSION_CONTRIBUTIONS': 'PENSION_CONTRIBUTIONS',

      // Canada
      'CA_INCOME_TAX': 'INCOME_TAX',
      'CA_PROVINCIAL_TAX': 'PROVINCIAL_TAX',
      'CA_CPP': 'CPP',
      'CA_EI': 'EI',

      // Australia
      'AU_INCOME_TAX': 'INCOME_TAX',
      'AU_MEDICARE_LEVY': 'MEDICARE_LEVY',
      'AU_SUPERANNUATION': 'SUPERANNUATION',

      // Germany
      'DE_INCOME_TAX': 'INCOME_TAX',
      'DE_SOCIAL_SECURITY': 'SOCIAL_SECURITY',
      'DE_HEALTH_INSURANCE': 'HEALTH_INSURANCE',
      'DE_UNEMPLOYMENT_INSURANCE': 'UNEMPLOYMENT_INSURANCE',
      'DE_PENSION_INSURANCE': 'PENSION_INSURANCE',
      'DE_LONG_TERM_CARE_INSURANCE': 'LONG_TERM_CARE_INSURANCE',
      'DE_SOLIDARITY_SURCHARGE': 'SOLIDARITY_SURCHARGE',

      // France
      'FR_INCOME_TAX': 'INCOME_TAX',
      'FR_SOCIAL_SECURITY': 'SOCIAL_SECURITY',
      'FR_HEALTH_INSURANCE': 'HEALTH_INSURANCE',
      'FR_UNEMPLOYMENT_INSURANCE': 'UNEMPLOYMENT_INSURANCE',
      'FR_PENSION_CONTRIBUTIONS': 'PENSION_CONTRIBUTIONS',

      // Japan
      'JP_INCOME_TAX': 'INCOME_TAX',
      'JP_RESIDENT_TAX': 'RESIDENT_TAX',
      'JP_HEALTH_INSURANCE': 'HEALTH_INSURANCE',
      'JP_PENSION_INSURANCE': 'PENSION_INSURANCE',
      'JP_UNEMPLOYMENT_INSURANCE': 'UNEMPLOYMENT_INSURANCE'
    };

    return mapping[clientTaxType] || 'OTHER';
  };

  // Map server-side enum values back to client-side tax type codes
  const mapTaxTypeFromServer = (serverTaxType, country) => {
    // Create reverse mapping for the specific country
    const reverseMapping = {};
    
    // Build reverse mapping from the forward mapping
    Object.entries({
      // US
      'US_INCOME_TAX': 'INCOME_TAX',
      'US_FEDERAL_WITHHOLDING': 'FEDERAL_WITHHOLDING',
      'US_STATE_INCOME_TAX': 'STATE_INCOME_TAX',
      'US_SOCIAL_SECURITY': 'SOCIAL_SECURITY',
      'US_MEDICARE': 'MEDICARE',

      // India
      'IN_INCOME_TAX': 'INCOME_TAX',
      'IN_PROFESSIONAL_TAX': 'PROFESSIONAL_TAX',
      'IN_TDS': 'TDS',
      'IN_PF': 'PF',
      'IN_ESIC': 'ESIC',
      'IN_LWF': 'LWF',

      // UK
      'UK_INCOME_TAX': 'INCOME_TAX',
      'UK_NATIONAL_INSURANCE': 'NATIONAL_INSURANCE',
      'UK_PENSION_CONTRIBUTIONS': 'PENSION_CONTRIBUTIONS',

      // Canada
      'CA_INCOME_TAX': 'INCOME_TAX',
      'CA_PROVINCIAL_TAX': 'PROVINCIAL_TAX',
      'CA_CPP': 'CPP',
      'CA_EI': 'EI',

      // Australia
      'AU_INCOME_TAX': 'INCOME_TAX',
      'AU_MEDICARE_LEVY': 'MEDICARE_LEVY',
      'AU_SUPERANNUATION': 'SUPERANNUATION',

      // Germany
      'DE_INCOME_TAX': 'INCOME_TAX',
      'DE_SOCIAL_SECURITY': 'SOCIAL_SECURITY',
      'DE_HEALTH_INSURANCE': 'HEALTH_INSURANCE',
      'DE_UNEMPLOYMENT_INSURANCE': 'UNEMPLOYMENT_INSURANCE',
      'DE_PENSION_INSURANCE': 'PENSION_INSURANCE',
      'DE_LONG_TERM_CARE_INSURANCE': 'LONG_TERM_CARE_INSURANCE',
      'DE_SOLIDARITY_SURCHARGE': 'SOLIDARITY_SURCHARGE',

      // France
      'FR_INCOME_TAX': 'INCOME_TAX',
      'FR_SOCIAL_SECURITY': 'SOCIAL_SECURITY',
      'FR_HEALTH_INSURANCE': 'HEALTH_INSURANCE',
      'FR_UNEMPLOYMENT_INSURANCE': 'UNEMPLOYMENT_INSURANCE',
      'FR_PENSION_CONTRIBUTIONS': 'PENSION_CONTRIBUTIONS',

      // Japan
      'JP_INCOME_TAX': 'INCOME_TAX',
      'JP_RESIDENT_TAX': 'RESIDENT_TAX',
      'JP_HEALTH_INSURANCE': 'HEALTH_INSURANCE',
      'JP_PENSION_INSURANCE': 'PENSION_INSURANCE',
      'JP_UNEMPLOYMENT_INSURANCE': 'UNEMPLOYMENT_INSURANCE'
    }).forEach(([clientCode, serverCode]) => {
      const clientCountry = clientCode.split('_')[0];
      if (clientCountry === country) {
        reverseMapping[serverCode] = clientCode;
      }
    });

    return reverseMapping[serverTaxType] || serverTaxType;
  };

  const getFilteredTaxTypesForForm = () => {
    if (!taxFormData.country || String(taxFormData.country).trim() === '') {
      // When no country is selected, show all unique tax types
      const uniqueTypes = [];
      const seen = new Set();

      taxTypeOptions.forEach(type => {
        const code = String(type.code || '').toUpperCase();
        if (code && !seen.has(code)) {
          seen.add(code);
          uniqueTypes.push(type);
        }
      });

      return uniqueTypes.sort((a, b) => getTaxTypeDisplayName(a).localeCompare(getTaxTypeDisplayName(b)));
    }

    // Accept either country code (e.g. 'IN') or country name (e.g. 'India')
    const input = String(taxFormData.country).trim().toUpperCase();
    const matchedCodes = countries
      .filter(c => c.code.toUpperCase() === input || c.name.toUpperCase() === input)
      .map(c => c.code.toUpperCase());

    // If nothing matched, assume the input itself may be a code (defensive)
    if (matchedCodes.length === 0 && input) matchedCodes.push(input);

    // Filter by any matched country codes, then dedupe by tax code
    const filtered = taxTypeOptions.filter(type =>
      type.country && matchedCodes.includes(String(type.country).toUpperCase())
    );

    const unique = [];
    const seen = new Set();
    filtered.forEach(type => {
      const code = String(type.code || '').toUpperCase();
      if (code && !seen.has(code)) {
        seen.add(code);
        unique.push(type);
      }
    });

    return unique.sort((a, b) => getTaxTypeDisplayName(a).localeCompare(getTaxTypeDisplayName(b)));
  };

  const getFilteredTaxTypes = () => {
    if (!taxFilters.country || String(taxFilters.country).trim() === '') {
      // When no country is selected, show all unique tax types
      const uniqueTypes = [];
      const seen = new Set();

      taxTypeOptions.forEach(type => {
        const code = String(type.code || '').toUpperCase();
        if (code && !seen.has(code)) {
          seen.add(code);
          uniqueTypes.push(type);
        }
      });

      return uniqueTypes.sort((a, b) => getTaxTypeDisplayName(a).localeCompare(getTaxTypeDisplayName(b)));
    }

    // Accept either country code or full country name
    const input = String(taxFilters.country).trim().toUpperCase();
    const matchedCodes = countries
      .filter(c => c.code.toUpperCase() === input || c.name.toUpperCase() === input)
      .map(c => c.code.toUpperCase());

    if (matchedCodes.length === 0 && input) matchedCodes.push(input);

    const filtered = taxTypeOptions.filter(type =>
      type.country && matchedCodes.includes(String(type.country).toUpperCase())
    );

    const unique = [];
    const seen = new Set();
    filtered.forEach(type => {
      const code = String(type.code || '').toUpperCase();
      if (code && !seen.has(code)) {
        seen.add(code);
        unique.push(type);
      }
    });

    return unique.sort((a, b) => getTaxTypeDisplayName(a).localeCompare(getTaxTypeDisplayName(b)));
  };

  const handleCreateTaxConfig = async () => {
    try {
      // Map the client-side tax type to server-side enum value
      const serverData = {
        ...taxFormData,
        taxType: mapTaxTypeToServer(taxFormData.taxType)
      };

      await createTaxConfig(serverData).unwrap();
      toast.success('Tax configuration created successfully');
      dispatch(setCreateModalOpen(false));
      dispatch(resetTaxFormData());
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to create tax configuration');
    }
  };

  const handleUpdateTaxConfig = async () => {
    try {
      // Map the client-side tax type to server-side enum value
      const serverData = {
        ...taxFormData,
        taxType: mapTaxTypeToServer(taxFormData.taxType)
      };

      await updateTaxConfig({ id: selectedConfigId, ...serverData }).unwrap();
      toast.success('Tax configuration updated successfully');
      dispatch(setEditModalOpen(false));
      dispatch(resetTaxFormData());
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update tax configuration');
    }
  };

  const handleDeleteTaxConfig = async () => {
    try {
      await deleteTaxConfig(selectedConfigId).unwrap();
      toast.success('Tax configuration deleted successfully');
      dispatch(setDeleteModalOpen(false));
      setSelectedConfigId(null);
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to delete tax configuration');
    }
  };

  const handleCalculateTax = async () => {
    try {
      const result = await calculateTax(calculationData).unwrap();
      toast.success('Tax calculated successfully');
      console.log('Tax calculation result:', result);
      // You can show the result in a modal or display it on the page
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to calculate tax');
    }
  };

  const openDeleteModal = (id) => {
    dispatch(setSelectedConfigId(id));
    dispatch(setDeleteModalOpen(true));
  };

  const openEditModal = (config) => {
    dispatch(setSelectedConfigId(config._id));
    // Format dates properly for HTML date inputs and map tax type back to frontend format
    const formattedConfig = {
      ...config,
      effectiveFrom: formatDateForInput(config.effectiveFrom),
      effectiveTo: formatDateForInput(config.effectiveTo),
      // Map server tax type back to client tax type based on country
      taxType: mapTaxTypeFromServer(config.taxType, config.country)
    };
    dispatch(updateTaxFormData(formattedConfig));
    dispatch(setEditModalOpen(true));
  };

  const openCreateModal = () => {
    dispatch(resetTaxFormData());
    // Set default effective from date to today
    const today = new Date().toISOString().split('T')[0];
    dispatch(updateTaxFormData({ effectiveFrom: today }));
    dispatch(setCreateModalOpen(true));
  };

  const formatCurrency = (amount, currency = companyCurrency) => {
    // Currency to locale mapping
    const currencyLocaleMap = {
      'USD': 'en-US',
      'INR': 'en-IN', 
      'EUR': 'en-EU',
      'GBP': 'en-GB',
      'CAD': 'en-CA',
      'AUD': 'en-AU',
      'JPY': 'ja-JP',
      'CNY': 'zh-CN'
    };

    const locale = currencyLocaleMap[currency] || 'en-US';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getCurrencySymbol = (currency = companyCurrency) => {
    const currencySymbols = {
      'USD': '$',
      'INR': '₹',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'JPY': '¥',
      'CNY': '¥'
    };
    
    return currencySymbols[currency] || '$';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format as YYYY-MM-DD for HTML date input
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Payroll Tax Configuration Master
            </h1>
            <p className="mt-2 text-gray-600 text-lg">
              Manage country and state-wise payroll tax configurations with flexible slab-based rules for employee salary deductions
            </p>
          </div>
          <div className="mt-6 sm:mt-0">
            <button
              onClick={() => openCreateModal()}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Tax Config
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-400/20 to-blue-600/20 backdrop-blur-xl border border-white/20 shadow-2xl p-6 transform hover:scale-105 transition-all duration-300">
            <div className="absolute top-4 right-4 w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-700 text-sm font-semibold mb-1">Total Configurations</p>
                <p className="text-3xl font-bold text-gray-800">{summaryStats.total}</p>
                <p className="text-xs text-cyan-600 mt-1">All records</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl shadow-lg">
                <DocumentTextIcon className="h-8 w-8 text-white" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-blue-500"></div>
          </div>

          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-400/20 to-green-600/20 backdrop-blur-xl border border-white/20 shadow-2xl p-6 transform hover:scale-105 transition-all duration-300">
            <div className="absolute top-4 right-4 w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-700 text-sm font-semibold mb-1">Active Configurations</p>
                <p className="text-3xl font-bold text-gray-800">{summaryStats.active}</p>
                <p className="text-xs text-emerald-600 mt-1">Currently active</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl shadow-lg">
                <CheckCircleSolidIcon className="h-8 w-8 text-white" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-green-500"></div>
          </div>

          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-400/20 to-purple-600/20 backdrop-blur-xl border border-white/20 shadow-2xl p-6 transform hover:scale-105 transition-all duration-300">
            <div className="absolute top-4 right-4 w-3 h-3 bg-violet-400 rounded-full animate-pulse"></div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-violet-700 text-sm font-semibold mb-1">Inactive Configurations</p>
                <p className="text-3xl font-bold text-gray-800">{summaryStats.inactive}</p>
                <p className="text-xs text-violet-600 mt-1">Needs attention</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-violet-400 to-purple-500 rounded-2xl shadow-lg">
                <XCircleIcon className="h-8 w-8 text-white" />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-violet-400 to-purple-500"></div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 mb-8 mt-8">
          <div className="p-8">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-6">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-0 bg-white/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-xl"></div>
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <div className="p-2 bg-gradient-to-br from-blue-400/20 to-purple-600/20 backdrop-blur-sm rounded-xl border border-white/20">
                    <MagnifyingGlassIcon className="h-4 w-4 text-blue-600" />
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Search by tax name, type, country, or state..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="relative block w-full pl-16 pr-12 py-3 bg-transparent border-0 rounded-2xl focus:outline-none focus:ring-0 text-sm font-medium text-gray-800 placeholder-gray-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

            {/* Filter Toggle and Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </button>

              {/* Export Options */}
              <div className="relative">
                <button
                  onClick={exportToCSV}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                  Export
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <select
                  value={taxFilters.country}
                  onChange={(e) => dispatch(updateTaxFilters({ country: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Countries</option>
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <select
                  value={taxFilters.state}
                  onChange={(e) => dispatch(updateTaxFilters({ state: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!taxFilters.country}
                >
                  <option value="">All States</option>
                  {states?.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Type
                </label>
                <select
                  value={taxFilters.taxType}
                  onChange={(e) => dispatch(updateTaxFilters({ taxType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {getFilteredTaxTypes().map((type) => (
                    <option key={type.code} value={type.code}>
                      {getTaxTypeDisplayName(type)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Active Filters */}
          {(searchTerm || taxFilters.country || taxFilters.state || taxFilters.taxType) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {searchTerm && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {taxFilters.country && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  Country: {countries.find(c => c.code === taxFilters.country)?.name}
                  <button
                    onClick={() => dispatch(updateTaxFilters({ country: '' }))}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {taxFilters.state && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                  State: {taxFilters.state}
                  <button
                    onClick={() => dispatch(updateTaxFilters({ state: '' }))}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {taxFilters.taxType && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                  Tax Type: {getTaxTypeDisplayName(taxFilters.taxType)}
                  <button
                    onClick={() => dispatch(updateTaxFilters({ taxType: '' }))}
                    className="ml-2 text-orange-600 hover:text-orange-800"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setSearchTerm('');
                  dispatch(resetTaxFilters());
                }}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tax Configurations Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tax Configuration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country/State
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effective Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2">Loading tax configurations...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium text-gray-900 mb-2">No tax configurations found</p>
                      <p className="text-sm text-gray-500">Get started by creating your first tax configuration</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedData.map((config) => (
                  <React.Fragment key={config._id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {config.taxName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getTaxTypeDisplayName(config.taxType)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {countries.find(c => c.code === config.country)?.name || config.country}
                        </div>
                        <div className="text-sm text-gray-500">
                          {config.state}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(config.effectiveFrom)}
                        </div>
                        <div className="text-sm text-gray-500">
                          to {config.effectiveTo ? formatDate(config.effectiveTo) : 'No End Date'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          config.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {config.isActive ? (
                            <CheckCircleSolidIcon className="h-4 w-4 mr-1" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 mr-1" />
                          )}
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setExpandedRows(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(config._id)) {
                                  newSet.delete(config._id);
                                } else {
                                  newSet.add(config._id);
                                }
                                return newSet;
                              });
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-50"
                            title="View Details"
                          >
                            {expandedRows.has(config._id) ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => openEditModal(config)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(config._id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              setCalculationData({
                                country: config.country,
                                state: config.state,
                                taxType: config.taxType,
                                salary: '',
                                date: new Date().toISOString().split('T')[0],
                              });
                              dispatch(setCalculationModalOpen(true));
                            }}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            title="Calculate Tax"
                          >
                            <CalculatorIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows.has(config._id) && (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Tax Slabs</h4>
                              <div className="space-y-2">
                                {config.slabs?.map((slab, index) => (
                                  <div key={index} className="text-sm text-gray-600 bg-white p-3 rounded border">
                                    <div className="flex justify-between">
                                      <span>
                                        {formatCurrency(slab.minSalary)} - {slab.maxSalary ? formatCurrency(slab.maxSalary) : 'Above'}
                                      </span>
                                      <span className="font-medium">
                                        {slab.percentage}% {slab.fixedAmount > 0 && `+ ${formatCurrency(slab.fixedAmount)}`}
                                      </span>
                                    </div>
                                    {slab.description && (
                                      <div className="text-xs text-gray-500 mt-1">{slab.description}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Details</h4>
                              <div className="space-y-1 text-sm text-gray-600">
                                <div>Description: {config.description || 'N/A'}</div>
                                <div>Created: {formatDate(config.createdAt)}</div>
                                <div>Updated: {formatDate(config.updatedAt)}</div>
                                {config.specialConditions?.length > 0 && (
                                  <div>
                                    <div className="font-medium mt-2">Special Conditions:</div>
                                    <ul className="list-disc list-inside ml-2">
                                      {config.specialConditions.map((condition, index) => (
                                        <li key={index} className="text-xs">
                                          {condition.condition}: {condition.percentage}%
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredData.length)}</span> of <span className="font-medium">{filteredData.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Previous</span>
                    ←
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="sr-only">Next</span>
                    →
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-6xl shadow-2xl rounded-2xl bg-white">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-3 mr-4">
                    <DocumentTextIcon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {isCreateModalOpen ? 'Create Tax Configuration' : 'Edit Tax Configuration'}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {isCreateModalOpen ? 'Set up a new tax configuration with custom slabs and rules' : 'Modify existing tax configuration settings'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    dispatch(setCreateModalOpen(false));
                    dispatch(setEditModalOpen(false));
                    dispatch(resetTaxFormData());
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="bg-blue-100 rounded-full p-2 mr-3">
                        <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      Basic Information
                    </h4>

                    <div className="space-y-4">
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tax Name *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={taxFormData.taxName}
                            onChange={(e) => dispatch(updateTaxFormData({ taxName: e.target.value }))}
                            className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            placeholder="e.g., Income Tax 2024"
                          />
                          <DocumentTextIcon className="absolute right-3 top-3.5 h-5 w-5 text-gray-400" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Country *
                          </label>
                          <div className="relative">
                            <select
                              value={taxFormData.country}
                              onChange={(e) => dispatch(updateTaxFormData({ country: e.target.value }))}
                              className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm appearance-none"
                            >
                              <option value="">Select Country</option>
                              {countries.map((country) => (
                                <option key={country.code} value={country.code}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                            <ChevronDownIcon className="absolute right-3 top-3.5 h-5 w-5 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            State *
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={taxFormData.state}
                              onChange={(e) => dispatch(updateTaxFormData({ state: e.target.value }))}
                              className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                              placeholder="e.g., California"
                            />
                            <XCircleIcon className="absolute right-3 top-3.5 h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tax Type *
                        </label>
                        <div className="relative">
                          <select
                            value={taxFormData.taxType}
                            onChange={(e) => dispatch(updateTaxFormData({ taxType: e.target.value }))}
                            className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm appearance-none"
                          >
                            <option value="">Select Tax Type</option>
                            {getFilteredTaxTypesForForm().map((type) => (
                              <option key={type.code} value={type.code}>
                                {getTaxTypeDisplayName(type)}
                              </option>
                            ))}
                          </select>
                          <ChevronDownIcon className="absolute right-3 top-3.5 h-5 w-5 text-gray-400 pointer-events-none" />
                        </div>
                        {taxFormData.country && (
                          <p className="text-xs text-blue-600 mt-2 flex items-center">
                            <CheckCircleSolidIcon className="h-4 w-4 mr-1" />
                            Showing {getFilteredTaxTypesForForm().length} tax types for {countries.find(c => c.code === taxFormData.country)?.name}
                          </p>
                        )}
                        {!taxFormData.country && (
                          <p className="text-xs text-amber-600 mt-2 flex items-center">
                            <XCircleIcon className="h-4 w-4 mr-1" />
                            Select a country to see relevant tax types
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Effective From *
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              value={taxFormData.effectiveFrom}
                              onChange={(e) => dispatch(updateTaxFormData({ effectiveFrom: e.target.value }))}
                              className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            />
                          </div>
                        </div>

                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Effective To
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              value={taxFormData.effectiveTo}
                              onChange={(e) => dispatch(updateTaxFormData({ effectiveTo: e.target.value }))}
                              className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={taxFormData.description}
                          onChange={(e) => dispatch(updateTaxFormData({ description: e.target.value }))}
                          rows={3}
                          className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm resize-none"
                          placeholder="Optional description"
                        />
                      </div>

                      <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <input
                          type="checkbox"
                          id="isActive"
                          checked={taxFormData.isActive}
                          onChange={(e) => dispatch(updateTaxFormData({ isActive: e.target.checked }))}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-200"
                        />
                        <label htmlFor="isActive" className="ml-3 block text-sm font-medium text-gray-900">
                          Mark as Active Configuration
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax Slabs */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                        <div className="bg-emerald-100 rounded-full p-2 mr-3">
                          <CalculatorIcon className="h-5 w-5 text-emerald-600" />
                        </div>
                        Tax Slabs Configuration
                      </h4>
                      <button
                        onClick={() => dispatch(addTaxSlab())}
                        className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                      >
                        <PlusIcon className="w-4 h-4 mr-2" />
                        Add Slab
                      </button>
                    </div>

                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {taxFormData.slabs.map((slab, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-all duration-200">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                              <div className="bg-emerald-100 rounded-full p-2 mr-3">
                                <span className="text-emerald-700 font-semibold text-sm">{index + 1}</span>
                              </div>
                              <span className="text-sm font-medium text-gray-700">Tax Slab {index + 1}</span>
                            </div>
                            {taxFormData.slabs.length > 1 && (
                              <button
                                onClick={() => dispatch(removeTaxSlab(index))}
                                className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-all duration-200"
                                title="Remove Slab"
                              >
                                <XMarkIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-2">
                                Min Salary *
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={slab.minSalary}
                                  onChange={(e) => dispatch(updateTaxSlab({
                                    index,
                                    data: { minSalary: parseFloat(e.target.value) || 0 }
                                  }))}
                                  className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm"
                                  placeholder="0"
                                />
                                <span className="absolute right-3 top-2.5 text-xs text-gray-500">{getCurrencySymbol()}</span>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-2">
                                Max Salary
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={slab.maxSalary || ''}
                                  onChange={(e) => dispatch(updateTaxSlab({
                                    index,
                                    data: { maxSalary: e.target.value ? parseFloat(e.target.value) : null }
                                  }))}
                                  className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm"
                                  placeholder="Leave empty for no limit"
                                />
                                <span className="absolute right-3 top-2.5 text-xs text-gray-500">{getCurrencySymbol()}</span>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-2">
                                Tax Rate (%) *
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={slab.percentage}
                                  onChange={(e) => dispatch(updateTaxSlab({
                                    index,
                                    data: { percentage: parseFloat(e.target.value) || 0 }
                                  }))}
                                  className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm"
                                  placeholder="0.00"
                                />
                                <span className="absolute right-3 top-2.5 text-xs text-gray-500">%</span>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-2">
                                Fixed Amount
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={slab.fixedAmount}
                                  onChange={(e) => dispatch(updateTaxSlab({
                                    index,
                                    data: { fixedAmount: parseFloat(e.target.value) || 0 }
                                  }))}
                                  className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm"
                                  placeholder="0.00"
                                />
                                <span className="absolute right-3 top-2.5 text-xs text-gray-500">{getCurrencySymbol()}</span>
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2">
                              Description
                            </label>
                            <input
                              type="text"
                              value={slab.description}
                              onChange={(e) => dispatch(updateTaxSlab({
                                index,
                                data: { description: e.target.value }
                              }))}
                              className="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm"
                              placeholder="Optional description"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    dispatch(setCreateModalOpen(false));
                    dispatch(setEditModalOpen(false));
                    dispatch(resetTaxFormData());
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={isCreateModalOpen ? handleCreateTaxConfig : handleUpdateTaxConfig}
                  disabled={isCreating || isUpdating}
                  className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 border border-transparent rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                >
                  {isCreating || isUpdating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {isCreating ? 'Creating...' : 'Updating...'}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <CheckCircleSolidIcon className="h-5 w-5 mr-2" />
                      {isCreateModalOpen ? 'Create Configuration' : 'Update Configuration'}
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-40 mx-auto p-6 border w-96 shadow-2xl rounded-2xl bg-white">
            <div className="text-center">
              <div className="bg-red-100 rounded-full p-4 mx-auto w-fit mb-4">
                <XCircleIcon className="h-12 w-12 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Tax Configuration</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete this tax configuration? This action cannot be undone and will permanently remove all associated data.
              </p>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => {
                    dispatch(setDeleteModalOpen(false));
                    dispatch(setSelectedConfigId(null));
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteTaxConfig}
                  disabled={isDeleting}
                  className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 border border-transparent rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                >
                  {isDeleting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <TrashIcon className="h-5 w-5 mr-2" />
                      Delete
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tax Calculation Modal */}
      {isCalculationModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-40 mx-auto p-6 border w-96 shadow-2xl rounded-2xl bg-white">
            <div className="text-center">
              <div className="bg-emerald-100 rounded-full p-4 mx-auto w-fit mb-4">
                <CalculatorIcon className="h-12 w-12 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Tax Calculator</h3>
              <p className="text-sm text-gray-600 mb-6">
                Calculate tax amount based on the selected configuration
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salary Amount *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={calculationData.salary}
                      onChange={(e) => setCalculationData({ ...calculationData, salary: e.target.value })}
                      className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                      placeholder="Enter salary amount"
                    />
                    <span className="absolute right-3 top-3.5 text-sm text-gray-500">{getCurrencySymbol()}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calculation Date
                  </label>
                  <input
                    type="date"
                    value={calculationData.date}
                    onChange={(e) => setCalculationData({ ...calculationData, date: e.target.value })}
                    className="w-full pl-4 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                  />
                </div>
              </div>

              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => dispatch(setCalculationModalOpen(false))}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCalculateTax}
                  disabled={isCalculating || !calculationData.salary}
                  className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-emerald-600 to-green-600 border border-transparent rounded-lg hover:from-emerald-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                >
                  {isCalculating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Calculating...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <CalculatorIcon className="h-5 w-5 mr-2" />
                      Calculate Tax
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TaxMaster;
